import _ from 'underscore';
import { AddressDoctorDialog } from 'app/blocks/address-doctor/__dialog/address-doctor__dialog';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import * as Utils from 'app/blocks/common/utils';
import * as middleware from 'app/blocks/middleware/middleware';
import PanelAdvancedMixin from 'app/blocks/Panel/advanced/panel_advanced.controller.mixin';
import panelDispatcher from 'app/blocks/Panel/panel.dispatcher';
import payment from 'app/pages/orders/orders.payment';
import PanelContactController from 'app/pages/orders/panel/contact/controller';
import PanelPaymentController from 'app/pages/orders/panel/payment/controller';

const panelNames = ['addresses', 'payment'];
const helpNotesIds = ['country'];

const OrdersOAInitiateController = section => {
    let p_;
    let renderCallback;
    const panelInfos = _.reduce(
        panelNames,
        (obj, name) => {
            // eslint-disable-next-line no-param-reassign
            obj[name] = {
                status: 'incomplete',
                open: section ? name === section : true,
            };
            return obj;
        },
        {},
    );
    const helpNotes = _.reduce(
        helpNotesIds,
        (obj, name) => {
            // eslint-disable-next-line no-param-reassign
            obj[name] = {
                visible: false,
                positionParent: null,
            };
            return obj;
        },
        {},
    );

    const self = {
        init(props, callback) {
            p_ = props;
            renderCallback = callback;
            self.renderCallback = callback;

            self.panelDispatcher = panelDispatcher.createDispatcher(self, name => {
                self.renderCallback();
                Utils.scrollToErrorField(name);
            });

            self.state = {
                isGoingNext: false,
                isPaymentMethodSupported: true,
                isSavingForLater: false,

                order: p_.getOrder(),
                updateProfileData: false,
                showHelpFunder: true,

                panels: panelInfos,
                helpNotes,
                socopayEnabled: p_.getSocopayEnabled(),
            };

            self.handleErrors(p_.errors);

            self.initChildControllers(props);
        },
        setState(update) {
            Object.assign(self.state, update);
            renderCallback();
        },
        handleErrors(errors) {
            if (!errors) return;

            if (errors.addresses) {
                self.panelDispatcher.markAsIncomplete('addresses');
                self.panelDispatcher.setValidationResults('addresses', errors.addresses);
            }
        },
        updStatePropHandler: propName => newState => self.setState({ [propName]: newState }),
        updateOrderState: update => {
            self.setState({
                order: {
                    ...self.state.order,
                    ...update,
                },
            });
        },
        async commitAddresses(addresses) {
            self.contactPanel.ctrl.updateMainAddress(addresses.mainAddress);
            self.updateOrderState({ billingAddress: addresses.mainAddress });
        },
        onPanelStatusChanged() {
            if (this.panelDispatcher.getPanelInfo(this.panelName).status === 'complete') {
                const notConfirmedPanels = Utils.getUncompletedPanels(self.panels);
                if (notConfirmedPanels.length > 0) {
                    notConfirmedPanels[0].openPanel();
                    notConfirmedPanels[0].scrollToPanel();
                }
            }
        },

        initChildControllers() {
            self.panels = [];

            self.paymentPanel = PanelAdvancedMixin({
                panelName: 'payment',
                panelDispatcher: self.panelDispatcher,
                onPanelStatusChanged: this.onPanelStatusChanged,
                commitHandler: panel => {
                    self.updateOrderState({ paymentDetails: panel.ctrl.state.paymentDetails });
                },
                rollbackHandler: panel =>
                    panel.ctrl.selectPayment(
                        panel.ctrl.paymentMethodsAvailable.length === 1
                            ? { paymentMethod: panel.ctrl.paymentMethodsAvailable[0] }
                            : self.state.order.paymentDetails,
                    ),
                ctrlFactory: propName => {
                    const ctrl = PanelPaymentController(
                        {
                            isOrderExisting: self.state.order.existing,
                            paymentMethods:
                                self.state.order.billingAddress.countryCode === 'CN' && self.state.socopayEnabled
                                    ? payment.oaPaymentMethodsWithAlipay
                                    : payment.oaPaymentMethods,
                            paymentMethodTransitions:
                                self.state.order.billingAddress.countryCode === 'CN' && self.state.socopayEnabled
                                    ? payment.oaTransitionsWithAlipay
                                    : payment.oaTransitions,
                            initOrder: Utils.cloneDeepSimple(self.state.order),
                        },
                        newState => {
                            self.setState({
                                [propName]: newState,
                                isPaymentMethodSupported: ctrl.isPaymentMethodSupportedByBrowser(),
                            });
                        },
                    );

                    return ctrl;
                },
            });
            self.panels.push(self.paymentPanel);

            self.contactPanel = PanelAdvancedMixin({
                panelName: 'addresses',
                panelDispatcher: self.panelDispatcher,
                onPanelStatusChanged: this.onPanelStatusChanged,
                commitHandler: async (panel, options = {}) => {
                    const addresses = await panel.ctrl.updateAddressWithApprove(
                        { mainAddress: self.state.order.billingAddress },
                        panel.ctrl.getAddresses(),
                        !options.skipAddressDoctor ? AddressDoctorDialog : null,
                    );
                    self.commitAddresses(addresses);
                    self.updateOrderState({ vatTaxDetails: panel.ctrl.state.vatTaxDetails });
                },
                rollbackHandler: panel => {
                    panel.ctrl.selectAddresses({
                        mainAddress: self.state.order.billingAddress,
                    });
                    panel.ctrl.selectVatTax(self.state.order.vatTaxDetails);
                },
                ctrlFactory: propName => {
                    return PanelContactController(
                        {
                            profileAddresses: p_.profileAddresses,
                            emailDetails: p_.emailDetails,
                            disableDifferentCountrySelection: true,
                            getOrder: () => self.state.order,
                        },
                        self.updStatePropHandler(propName),
                    );
                },
            });
            self.panels.push(self.contactPanel);

            self.panels.forEach(it => {
                it.rollback();
                self.updatePanelStatus(it);
            });
        },
        async updatePanelStatus(panel) {
            if (panel.panelName === p_.section) return;
            if (panel.getPanelInfo().validationResults) return;

            if (self.state.order.correlationId) {
                try {
                    await panel.confirm();
                } catch {
                    panel.clean();
                }
            } else if (!self.state.order.existing && _.isEqual(panel.panelName, 'addresses')) {
                panel.closeAndMarkCompleted();
            }
        },
        async saveForLater() {
            try {
                self.setState({ isSavingForLater: true });
                await self.flushShownData();
                await p_.onSaveForLater();
            } finally {
                self.setState({ isSavingForLater: false });
            }
        },
        async nextPage() {
            try {
                self.setState({ isGoingNext: true });

                await self.validateAndFlushShownData();
                const order = await middleware.openaccess.saveAndPreviewOrder(self.state.order);

                self.setState({ order });
                p_.onInitiateComplete(order);
            } catch (error) {
                if (error.code !== 'validation_error') {
                    showDialog.addErrorMessageDialog(error);
                }
            } finally {
                self.setState({ isGoingNext: false });
            }
        },
        flushShownData() {
            return Promise.all(self.panels.map(it => it.commit({ skipAddressDoctor: true })));
        },
        async validateAndFlushShownData() {
            try {
                await Promise.all(self.panels.map(it => it.confirm()));
            } catch (error) {
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw { code: 'validation_error' };
            }
        },
        addressToggleEventDone(visible) {
            if (visible) self.panelDispatcher.toggleHelpNote('country', null, true);
        },
        addressToggleEventStart(visible) {
            if (visible) self.panelDispatcher.toggleHelpNote('country', null, false);
        },
        showPanel(type) {
            self.panelDispatcher.togglePanel(type, false);
            self.scrollTo(type);
        },
        scrollTo(type) {
            self.panels.find(panel => panel.panelName === type).scrollToPanel();
        },
        isProcessing() {
            return self.panels.some(x => x.getPanelInfo().locked);
        },
    };

    return self;
};

export default OrdersOAInitiateController;
